import { useEffect, useRef, useState } from "react";

const API = "/api";
const token = () => localStorage.getItem("token");

type TipoVenta = "contado" | "credito";

export default function Facturacion() {
  const [tipo, setTipo] = useState<TipoVenta>("contado");

  const [clientes, setClientes] = useState<any[]>([]);
  const [vendedores, setVendedores] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);

  const [tipoCliente, setTipoCliente] = useState<"registrado" | "ocasional">("ocasional");
  const [clienteId, setClienteId] = useState("");
  const [clienteOcasional, setClienteOcasional] = useState("");
  const [vendedorId, setVendedorId] = useState("");
  const [metodoPago, setMetodoPago] = useState("efectivo");

  const [busqueda, setBusqueda] = useState("");
  const [carrito, setCarrito] = useState<any[]>([]);
  const [notas, setNotas] = useState("");
  const [costoEnvio, setCostoEnvio] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState<any>(null);

  const [abonoInicial, setAbonoInicial] = useState("");
  const [metodoPagoInicial, setMetodoPagoInicial] = useState("efectivo");

  const [mostrarCustom, setMostrarCustom] = useState(false);
  const [customForm, setCustomForm] = useState({ nombre: "", descripcion: "", precio: "", cantidad: "1", imagen_url: "" });
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  const [mostrarGaleria, setMostrarGaleria] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [mostrarClienteDropdown, setMostrarClienteDropdown] = useState(false);
  const clienteBlurTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetch(`${API}/creditos/clientes?activo=true`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => Array.isArray(d) && setClientes(d)).catch(() => {});
    fetch(`${API}/contado/vendedores?activo=true`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => Array.isArray(d) && setVendedores(d)).catch(() => {});
    fetch(`${API}/productos/`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json()).then(d => Array.isArray(d) && setProductos(d)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tipo === "credito" && tipoCliente === "ocasional") {
      setTipoCliente("registrado");
      setClienteOcasional("");
    }
  }, [tipo]);

  const productosFiltrados = productos.filter(p => p.nombre.toLowerCase().includes(busqueda.toLowerCase())).slice(0, 8);

  const clientesFiltrados = clientes.filter(c => {
    const q = busquedaCliente.toLowerCase().trim();
    if (!q) return true;
    return c.nombre?.toLowerCase().includes(q) || c.cedula?.toLowerCase().includes(q);
  }).slice(0, 8);

  const seleccionarCliente = (c: any) => {
    setClienteId(String(c.id));
    setBusquedaCliente(`${c.nombre}${c.cedula ? ` (${c.cedula})` : ""}`);
    setMostrarClienteDropdown(false);
  };

  const limpiarClienteSeleccionado = () => {
    setClienteId("");
    setBusquedaCliente("");
  };

  const agregarProducto = (p: any) => {
    const existente = carrito.find(i => i.producto_id === p.id);
    if (existente) {
      setCarrito(carrito.map(i => i.producto_id === p.id ? { ...i, cantidad: i.cantidad + 1 } : i));
    } else {
      setCarrito([...carrito, { key: `p-${p.id}`, producto_id: p.id, nombre: p.nombre, precio: p.precio_oferta || p.precio, cantidad: 1, stock: p.stock, esCustom: false }]);
    }
    setBusqueda("");
  };

  const agregarCustom = () => {
    if (!customForm.nombre || !customForm.precio) return;
    setCarrito([...carrito, {
      key: `c-${Date.now()}`, producto_id: null, nombre: customForm.nombre, descripcion: customForm.descripcion,
      precio: Number(customForm.precio), cantidad: Number(customForm.cantidad) || 1,
      imagen_url: customForm.imagen_url || null, esCustom: true,
    }]);
    setCustomForm({ nombre: "", descripcion: "", precio: "", cantidad: "1", imagen_url: "" });
    setMostrarCustom(false);
  };

  const subirFoto = async (file: File) => {
    setSubiendoImagen(true);
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${API}/creditos/upload-imagen`, { method: "POST", headers: { Authorization: `Bearer ${token()}` }, body: fd });
    setSubiendoImagen(false);
    if (r.ok) {
      const data = await r.json();
      setCustomForm(f => ({ ...f, imagen_url: data.imagen_url }));
    } else {
      alert("No se pudo subir la imagen");
    }
  };

  const cambiarCantidad = (key: string, cantidad: number) =>
    setCarrito(carrito.map(i => i.key === key ? { ...i, cantidad: Math.max(1, cantidad) } : i));
  const quitar = (key: string) => setCarrito(carrito.filter(i => i.key !== key));

  const subtotalProductos = carrito.reduce((s, i) => s + i.precio * i.cantidad, 0);
  const envio = Number(costoEnvio) || 0;
  const total = subtotalProductos + envio;

  const clienteValido = tipo === "credito"
    ? !!clienteId
    : (tipoCliente === "registrado" ? !!clienteId : !!clienteOcasional.trim());

  const limpiarFormulario = () => {
    setCarrito([]); setClienteId(""); setClienteOcasional(""); setBusquedaCliente("");
    setNotas(""); setCostoEnvio(""); setAbonoInicial(""); setMetodoPagoInicial("efectivo");
    fetch(`${API}/productos/`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r2 => r2.json()).then(d => Array.isArray(d) && setProductos(d)).catch(() => {});
  };

  const confirmarVenta = async () => {
    if (!clienteValido || carrito.length === 0) return;
    setEnviando(true);
    setMensaje(null);
    const items = carrito.map(i => i.esCustom ? {
      producto_id: null, nombre_personalizado: i.nombre, descripcion: i.descripcion || null,
      precio_unitario: i.precio, imagen_url: i.imagen_url || null, cantidad: i.cantidad,
    } : { producto_id: i.producto_id, cantidad: i.cantidad });

    if (tipo === "contado") {
      const r = await fetch(`${API}/contado/ventas`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          cliente_id: tipoCliente === "registrado" ? Number(clienteId) : null,
          cliente_nombre_ocasional: tipoCliente === "ocasional" ? clienteOcasional : null,
          vendedor_id: vendedorId ? Number(vendedorId) : null,
          items, metodo_pago: metodoPago, notas, costo_envio: envio,
        }),
      });
      const data = await r.json();
      setEnviando(false);
      if (r.ok) {
        setMensaje({ tipo: "ok", texto: `Venta de contado #${data.id} registrada. Total: ₡${data.total?.toLocaleString()}` });
        limpiarFormulario();
      } else {
        setMensaje({ tipo: "error", texto: data.detail || "No se pudo registrar la venta" });
      }
    } else {
      const r = await fetch(`${API}/creditos/ventas`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          cliente_id: Number(clienteId), items, notas,
          abono_inicial: abonoInicial ? Number(abonoInicial) : null,
          metodo_pago_inicial: metodoPagoInicial,
          vendedor_id: vendedorId ? Number(vendedorId) : null,
          costo_envio: envio,
        }),
      });
      const data = await r.json();
      setEnviando(false);
      if (r.ok) {
        setMensaje({ tipo: "ok", texto: `Venta #${data.id} registrada. Saldo: ₡${data.saldo_pendiente?.toLocaleString()}.${data.proxima_fecha_pago ? ` Próximo pago: ${data.proxima_fecha_pago}` : " Pagada por completo."}` });
        limpiarFormulario();
      } else {
        setMensaje({ tipo: "error", texto: data.detail || "No se pudo registrar la venta" });
      }
    }
  };

  const input = { width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "14px", fontFamily: "Nunito,sans-serif", boxSizing: "border-box" as const };
  const label = { display: "block", fontSize: "12px", fontWeight: 700, color: "#6b7280", marginBottom: "4px" };
  const colorTipo = tipo === "contado" ? "#22c55e" : "#0D47A1";

  return (
    <div>
      <h1 style={{ fontFamily: "Montserrat,sans-serif", fontSize: "24px", fontWeight: 900, marginBottom: "16px", color: "#1a1a2e" }}>
        Facturación
      </h1>

      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", maxWidth: "420px" }}>
        <button onClick={() => setTipo("credito")}
          style={{ flex: 1, background: tipo === "credito" ? "#0D47A1" : "#F9FAFB", color: tipo === "credito" ? "#fff" : "#374151", border: "none", borderRadius: "10px", padding: "12px", fontWeight: 800, fontSize: "14px", cursor: "pointer", fontFamily: "Nunito,sans-serif" }}>
          🧾 Venta a Crédito
        </button>
        <button onClick={() => setTipo("contado")}
          style={{ flex: 1, background: tipo === "contado" ? "#22c55e" : "#F9FAFB", color: tipo === "contado" ? "#fff" : "#374151", border: "none", borderRadius: "10px", padding: "12px", fontWeight: 800, fontSize: "14px", cursor: "pointer", fontFamily: "Nunito,sans-serif" }}>
          💵 Venta de Contado
        </button>
      </div>

      {mensaje && (
        <div style={{ background: mensaje.tipo === "ok" ? "#ECFDF5" : "#FEF2F2", color: mensaje.tipo === "ok" ? "#065F46" : "#991B1B", padding: "14px 18px", borderRadius: "12px", marginBottom: "20px", fontWeight: 600, fontSize: "14px" }}>
          {mensaje.texto}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .fact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="fact-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "20px" }}>
        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <label style={label}>Cliente *</label>

          {tipo === "contado" && (
            <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
              <button onClick={() => setTipoCliente("ocasional")}
                style={{ flex: 1, background: tipoCliente === "ocasional" ? "#0D47A1" : "#F9FAFB", color: tipoCliente === "ocasional" ? "#fff" : "#374151", border: "none", borderRadius: "8px", padding: "8px", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                Cliente ocasional
              </button>
              <button onClick={() => setTipoCliente("registrado")}
                style={{ flex: 1, background: tipoCliente === "registrado" ? "#0D47A1" : "#F9FAFB", color: tipoCliente === "registrado" ? "#fff" : "#374151", border: "none", borderRadius: "8px", padding: "8px", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                Cliente registrado
              </button>
            </div>
          )}

          {tipoCliente === "ocasional" && tipo === "contado" ? (
            <input style={{ ...input, marginBottom: "16px" }} placeholder="Nombre del cliente"
              value={clienteOcasional} onChange={e => setClienteOcasional(e.target.value)} />
          ) : (
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <input
                style={input}
                placeholder="Buscar cliente por nombre o cédula..."
                value={busquedaCliente}
                onChange={e => {
                  setBusquedaCliente(e.target.value);
                  setMostrarClienteDropdown(true);
                  if (clienteId) setClienteId("");
                }}
                onFocus={() => setMostrarClienteDropdown(true)}
                onBlur={() => {
                  clienteBlurTimeout.current = setTimeout(() => setMostrarClienteDropdown(false), 150);
                }}
              />
              {clienteId && (
                <button type="button" onClick={limpiarClienteSeleccionado}
                  style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "16px" }}>
                  ×
                </button>
              )}
              {mostrarClienteDropdown && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "10px", boxShadow: "0 4px 14px rgba(0,0,0,0.1)", maxHeight: "220px", overflowY: "auto", zIndex: 20 }}>
                  {clientesFiltrados.length > 0 ? clientesFiltrados.map(c => (
                    <div key={c.id}
                      onMouseDown={() => {
                        if (clienteBlurTimeout.current) clearTimeout(clienteBlurTimeout.current);
                        seleccionarCliente(c);
                      }}
                      style={{ padding: "10px 14px", cursor: "pointer", fontSize: "14px", borderBottom: "1px solid #f3f4f6" }}
                      onMouseEnter={e => (e.currentTarget.style.background = "#F9FAFB")}
                      onMouseLeave={e => (e.currentTarget.style.background = "#fff")}>
                      {c.nombre}{c.cedula ? ` (${c.cedula})` : ""}
                    </div>
                  )) : (
                    <div style={{ color: "#9ca3af", fontSize: "13px", padding: "10px 14px" }}>Sin resultados</div>
                  )}
                </div>
              )}
            </div>
          )}

          <label style={label}>Vendedor (opcional)</label>
          <select style={{ ...input, marginBottom: "16px" }} value={vendedorId} onChange={e => setVendedorId(e.target.value)}>
            <option value="">-- Sin asignar --</option>
            {vendedores.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
          </select>

          <label style={label}>Buscar producto del catálogo</label>
          <input style={input} placeholder="Escribí el nombre del producto..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          {busqueda && (
            <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {productosFiltrados.map(p => (
                <div key={p.id} onClick={() => agregarProducto(p)}
                  style={{ padding: "10px 14px", background: "#F9FAFB", borderRadius: "10px", cursor: "pointer", display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                  <span>{p.nombre}</span>
                  <span style={{ color: p.stock > 0 ? "#6b7280" : "#ef4444" }}>₡{(p.precio_oferta || p.precio)?.toLocaleString()} · stock {p.stock}</span>
                </div>
              ))}
              {productosFiltrados.length === 0 && <div style={{ color: "#9ca3af", fontSize: "13px", padding: "8px" }}>Sin resultados</div>}
            </div>
          )}

          <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px dashed #e5e7eb" }}>
            <button onClick={() => setMostrarCustom(!mostrarCustom)}
              style={{ background: "#FFF7ED", color: "#C2410C", border: "1.5px dashed #FDBA74", borderRadius: "10px", padding: "10px 16px", fontWeight: 700, fontSize: "13px", cursor: "pointer", width: "100%", fontFamily: "Nunito,sans-serif" }}>
              {mostrarCustom ? "Cancelar" : "➕ Producto fuera de catálogo"}
            </button>
            {mostrarCustom && (
              <div style={{ marginTop: "12px", background: "#FFF7ED", borderRadius: "12px", padding: "16px" }}>
                <label style={label}>Nombre / descripción rápida *</label>
                <input style={{ ...input, marginBottom: "10px" }} value={customForm.nombre} onChange={e => setCustomForm({ ...customForm, nombre: e.target.value })} />
                <label style={label}>Detalle adicional (opcional)</label>
                <input style={{ ...input, marginBottom: "10px" }} value={customForm.descripcion} onChange={e => setCustomForm({ ...customForm, descripcion: e.target.value })} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
                  <div>
                    <label style={label}>Monto *</label>
                    <input type="number" style={input} value={customForm.precio} onChange={e => setCustomForm({ ...customForm, precio: e.target.value })} />
                  </div>
                  <div>
                    <label style={label}>Cantidad</label>
                    <input type="number" min={1} style={input} value={customForm.cantidad} onChange={e => setCustomForm({ ...customForm, cantidad: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <button onClick={() => fileInputRef.current?.click()} disabled={subiendoImagen}
                    style={{ flex: 1, background: "#fff", border: "1.5px solid #FDBA74", borderRadius: "8px", padding: "8px", fontSize: "12px", fontWeight: 700, cursor: "pointer", color: "#C2410C" }}>
                    {subiendoImagen ? "Subiendo..." : "📷 Subir foto"}
                  </button>
                  <button onClick={() => setMostrarGaleria(!mostrarGaleria)}
                    style={{ flex: 1, background: "#fff", border: "1.5px solid #FDBA74", borderRadius: "8px", padding: "8px", fontSize: "12px", fontWeight: 700, cursor: "pointer", color: "#C2410C" }}>
                    🖼️ Usar foto existente
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => e.target.files && e.target.files[0] && subirFoto(e.target.files[0])} />
                {mostrarGaleria && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px", marginBottom: "8px", maxHeight: "160px", overflowY: "auto" }}>
                    {productos.filter(p => p.imagen_url).map(p => (
                      <img key={p.id} src={p.imagen_url} title={p.nombre}
                        onClick={() => { setCustomForm(f => ({ ...f, imagen_url: p.imagen_url })); setMostrarGaleria(false); }}
                        style={{ width: "100%", height: "50px", objectFit: "cover", borderRadius: "6px", cursor: "pointer", border: customForm.imagen_url === p.imagen_url ? "2px solid #C2410C" : "1px solid #e5e7eb" }} />
                    ))}
                  </div>
                )}
                {customForm.imagen_url && <img src={customForm.imagen_url} alt="" style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px", marginBottom: "8px" }} />}
                <button onClick={agregarCustom} disabled={!customForm.nombre || !customForm.precio}
                  style={{ width: "100%", background: "#C2410C", color: "#fff", border: "none", borderRadius: "8px", padding: "10px", fontWeight: 800, cursor: (customForm.nombre && customForm.precio) ? "pointer" : "not-allowed", opacity: (customForm.nombre && customForm.precio) ? 1 : 0.5, fontFamily: "Nunito,sans-serif" }}>
                  Agregar al carrito
                </button>
              </div>
            )}
          </div>

          <label style={{ ...label, marginTop: "16px" }}>Notas (opcional)</label>
          <textarea style={{ ...input, minHeight: "50px" }} value={notas} onChange={e => setNotas(e.target.value)} />
        </div>

        <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 800, fontSize: "16px", marginBottom: "12px" }}>Carrito ({carrito.length})</div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px", minHeight: "120px" }}>
            {carrito.map(i => (
              <div key={i.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px", background: i.esCustom ? "#FFF7ED" : "#F9FAFB", borderRadius: "10px" }}>
                {i.imagen_url && <img src={i.imagen_url} alt="" style={{ width: "34px", height: "34px", objectFit: "cover", borderRadius: "6px", marginRight: "8px" }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "13px" }}>{i.nombre}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>₡{i.precio.toLocaleString()} c/u</div>
                </div>
                <input type="number" min={1} value={i.cantidad} onChange={e => cambiarCantidad(i.key, Number(e.target.value))}
                  style={{ width: "50px", padding: "6px", borderRadius: "6px", border: "1px solid #e5e7eb", textAlign: "center", marginRight: "8px" }} />
                <div style={{ fontWeight: 800, fontSize: "13px", width: "80px", textAlign: "right" }}>₡{(i.precio * i.cantidad).toLocaleString()}</div>
                <button onClick={() => quitar(i.key)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: "18px", marginLeft: "8px" }}>×</button>
              </div>
            ))}
            {carrito.length === 0 && <div style={{ color: "#9ca3af", fontSize: "13px", textAlign: "center", padding: "20px" }}>Agregá productos desde la izquierda</div>}
          </div>

          <div style={{ borderTop: "2px solid #f3f4f6", marginTop: "16px", paddingTop: "16px" }}>
            <label style={label}>Costo de envío (opcional)</label>
            <input type="number" min={0} placeholder="₡0" value={costoEnvio} onChange={e => setCostoEnvio(e.target.value)}
              style={{ ...input, marginBottom: "16px" }} />

            {tipo === "contado" ? (
              <>
                <label style={label}>Método de pago</label>
                <select style={{ ...input, marginBottom: "16px" }} value={metodoPago} onChange={e => setMetodoPago(e.target.value)}>
                  <option value="efectivo">Efectivo</option>
                  <option value="sinpe">Sinpe Móvil</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                </select>
              </>
            ) : (
              <>
                <label style={label}>Enganche / pago inicial (opcional)</label>
                <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                  <input type="number" min={0} max={total} placeholder="₡0" value={abonoInicial}
                    onChange={e => setAbonoInicial(e.target.value)} style={{ ...input, flex: 1 }} />
                  <select value={metodoPagoInicial} onChange={e => setMetodoPagoInicial(e.target.value)} style={{ ...input, flex: 1 }}>
                    <option value="efectivo">Efectivo</option>
                    <option value="sinpe">Sinpe Móvil</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select>
                </div>
              </>
            )}

            {envio > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px", color: "#6b7280" }}>
                <span>Subtotal productos</span>
                <span>₡{subtotalProductos.toLocaleString()}</span>
              </div>
            )}
            {envio > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px", color: "#6b7280" }}>
                <span>Costo de envío</span>
                <span>₡{envio.toLocaleString()}</span>
              </div>
            )}

            {tipo === "contado" ? (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                <span style={{ fontWeight: 700 }}>Total</span>
                <span style={{ fontFamily: "Montserrat,sans-serif", fontWeight: 900, fontSize: "22px", color: colorTipo }}>₡{total.toLocaleString()}</span>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px", color: "#6b7280" }}>
                  <span>Total de la venta</span>
                  <span>₡{total.toLocaleString()}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                  <span style={{ fontWeight: 700 }}>Saldo a crédito</span>
                  <span style={{ fontFamily: "Montserrat,sans-serif", fontWeight: 900, fontSize: "22px", color: colorTipo }}>
                    ₡{Math.max(0, total - (Number(abonoInicial) || 0)).toLocaleString()}
                  </span>
                </div>
              </>
            )}

            <button onClick={confirmarVenta} disabled={!clienteValido || carrito.length === 0 || enviando}
              style={{ width: "100%", background: colorTipo, color: "#fff", border: "none", borderRadius: "12px", padding: "14px", fontWeight: 800, fontSize: "15px", cursor: (!clienteValido || carrito.length === 0) ? "not-allowed" : "pointer", opacity: (!clienteValido || carrito.length === 0) ? 0.5 : 1, fontFamily: "Nunito,sans-serif" }}>
              {enviando ? "Procesando..." : tipo === "contado" ? "Confirmar Venta de Contado" : "Confirmar Venta a Crédito"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
